/* ============================================================
   Dathent — Admin Plans & limits (/app/admin/plans)
   Gated server-side by requireAdmin() (auth-worker proxies to the
   stripe-worker, which owns billing_plans + billing_plan_prices +
   billing_plan_config_versions — docs/TARIFF-REWORK-BRIEF.md §3).
   Edit each plan's display price, usage limits and feature flags,
   manage Stripe price mappings, and roll back to an earlier version.

   Security is 100% server-side (auth-worker requireAdmin); this
   screen carries no client guard, mirroring AdminBroadcast /
   AdminActivation. No optimistic UI — every mutation re-fetches the
   plan list from the server response so the screen never shows a
   state the backend hasn't confirmed.
   ============================================================ */
import React, { useCallback, useEffect, useState } from 'react';
import { api, DathentAPIError } from '../services/api/client.js';

// free / free_no_card are structurally non-editable server-side (no
// billing_plans row / hard-rejected by writePlan()). They never appear in
// the GET response's `plans` array today, but guard defensively anyway.
const ALL_USAGE_METRICS = [
  'publish_ready_posts_monthly',
  'premium_visuals_monthly',
  'content_plan_regenerations_monthly',
  'assistant_turns_monthly',
  'ai_generations_monthly',
  'image_generations_monthly',
  'carousel_generations_monthly',
  'scheduled_posts_monthly',
  'published_posts_monthly',
  'social_accounts_total',
  'brand_profiles_total',
  'mcp_requests_monthly',
  'research_runs_monthly',
  'manual_posts_monthly',
  'caption_assists_monthly',
  'video_reels_monthly',
];

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return String(iso).slice(0, 16);
  }
}

function fmtCents(cents, currency) {
  if (cents == null) return '—';
  const cur = (currency || 'eur').toUpperCase();
  return `${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${cur}`;
}

function errMsg(e) {
  if (e instanceof DathentAPIError) return e.message || 'Request failed';
  return e?.message || 'Request failed';
}

const card = {
  background: 'var(--surface, #16161a)',
  border: '1px solid var(--border, #2a2a30)',
  borderRadius: 14,
  padding: 20,
};
const subCard = {
  background: 'var(--surface-2, #0f0f12)',
  border: '1px solid var(--border, #2a2a30)',
  borderRadius: 10,
  padding: 14,
  marginTop: 14,
};
const label = { display: 'block', fontSize: 12, color: 'var(--text-muted, #9a9aa2)', margin: '10px 0 4px' };
const input = {
  width: '100%', boxSizing: 'border-box', background: 'var(--surface-2, #0f0f12)',
  border: '1px solid var(--border, #2a2a30)', borderRadius: 10, color: 'var(--text, #ececf0)',
  padding: '9px 11px', fontSize: 14, fontFamily: 'inherit',
};
const btn = (disabled) => ({
  background: 'var(--gold, #caa24a)', color: '#0b0b0c', border: 0, borderRadius: 10,
  padding: '9px 15px', fontSize: 13.5, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.55 : 1,
});
const btnGhost = (disabled) => ({
  background: 'transparent', color: 'var(--text, #ececf0)', border: '1px solid var(--border, #2a2a30)',
  borderRadius: 10, padding: '9px 15px', fontSize: 13.5, fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.55 : 1,
});
const btnDanger = (disabled) => ({
  background: 'transparent', color: '#e8a59f', border: '1px solid #e0685f88', borderRadius: 10,
  padding: '7px 13px', fontSize: 13, fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.55 : 1,
});

function ErrorNote({ error }) {
  if (!error) return null;
  return (
    <div style={{
      marginTop: 12, padding: '10px 12px', borderRadius: 10, fontSize: 13,
      background: '#e0685f18', border: '1px solid #e0685f55', color: '#e8a59f',
    }}>{error}</div>
  );
}

function AmberNote({ children }) {
  return (
    <div style={{
      marginTop: 12, padding: '10px 12px', borderRadius: 10, fontSize: 12.5,
      background: '#caa24a18', border: '1px solid #caa24a55', color: '#e7c878', lineHeight: 1.5,
    }}>{children}</div>
  );
}

function DiffSummary({ applied }) {
  if (!applied || Object.keys(applied).length === 0) return null;
  return (
    <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--text-muted, #9a9aa2)' }}>
      <div style={{ marginBottom: 4, color: 'var(--text, #ececf0)', fontWeight: 600 }}>Applied changes</div>
      {Object.entries(applied).map(([key, { old: oldVal, new: newVal }]) => (
        <div key={key} style={{ padding: '3px 0' }}>
          <strong>{key}</strong>: {JSON.stringify(oldVal)} → {JSON.stringify(newVal)}
        </div>
      ))}
    </div>
  );
}

// Prompt-style modal for a required changeReason before any write. Mirrors
// AdminBroadcast's type-to-confirm pattern but generalized (used by Save,
// price upsert, and Rollback — the last two get an extra danger tone).
function ChangeReasonModal({ title, danger, confirmLabel, onCancel, onConfirm, busy }) {
  const [reason, setReason] = useState('');
  const [err, setErr] = useState(null);

  const submit = () => {
    if (!reason.trim()) { setErr('A change reason is required.'); return; }
    setErr(null);
    onConfirm(reason.trim());
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
    }}>
      <div style={{ ...card, maxWidth: 440, width: '100%', border: danger ? '1px solid #e0685f88' : card.border }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 16, color: danger ? '#e8a59f' : 'var(--text, #ececf0)' }}>{title}</h3>
        <label style={label}>Change reason (required)</label>
        <textarea
          style={{ ...input, minHeight: 70, resize: 'vertical' }}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why is this change being made?"
          autoFocus
        />
        <ErrorNote error={err} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" style={btnGhost(busy)} disabled={busy} onClick={onCancel}>Cancel</button>
          <button
            type="button"
            style={danger
              ? { ...btn(busy), background: '#e0685f', color: '#fff' }
              : btn(busy)}
            disabled={busy}
            onClick={submit}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── limits table ───────────────────────────────────────────────────────────

function LimitsTable({ limits, draft, setDraft, disabled }) {
  const setValue = (metric, value) => setDraft((cur) => ({ ...cur, [metric]: value }));

  return (
    <div style={subCard}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Limits</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '6px 12px', alignItems: 'center' }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted, #9a9aa2)', textTransform: 'uppercase' }}>Metric</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted, #9a9aa2)', textTransform: 'uppercase' }}>Value</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted, #9a9aa2)', textTransform: 'uppercase' }}>Unlimited</div>
        {ALL_USAGE_METRICS.map((metric) => {
          const current = draft[metric];
          const isUnlimited = current === null;
          return (
            <React.Fragment key={metric}>
              <div style={{ fontSize: 13, fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>{metric}</div>
              <input
                type="number"
                min={0}
                max={1000000}
                style={{ ...input, width: 110, opacity: isUnlimited ? 0.4 : 1 }}
                value={isUnlimited ? '' : (current ?? '')}
                disabled={disabled || isUnlimited}
                placeholder={isUnlimited ? 'unlimited' : '0'}
                onChange={(e) => {
                  const v = e.target.value;
                  setValue(metric, v === '' ? 0 : Math.max(0, Math.min(1000000, parseInt(v, 10) || 0)));
                }}
              />
              <input
                type="checkbox"
                checked={isUnlimited}
                disabled={disabled}
                onChange={(e) => setValue(metric, e.target.checked ? null : (limits[metric] ?? 0))}
              />
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ─── features toggles ──────────────────────────────────────────────────────

function FeaturesToggles({ draft, setDraft, disabled }) {
  const keys = Object.keys(draft);
  const setValue = (key, value) => setDraft((cur) => ({ ...cur, [key]: value }));

  return (
    <div style={subCard}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Features</div>
      {keys.length === 0 ? (
        <p style={{ fontSize: 12.5, color: 'var(--text-muted, #9a9aa2)', margin: 0 }}>No feature flags configured for this plan.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {keys.map((key) => (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, cursor: disabled ? 'default' : 'pointer' }}>
              <input
                type="checkbox"
                checked={Boolean(draft[key])}
                disabled={disabled}
                onChange={(e) => setValue(key, e.target.checked)}
              />
              <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>{key}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── prices section ─────────────────────────────────────────────────────────

function PricesSection({ planKey, prices, priceDriftWarning, disabled, onSaved, onError }) {
  const [form, setForm] = useState({ priceKey: '', stripePriceId: '', amountCents: '', currency: 'eur', billingInterval: 'monthly', isDefault: false });
  const [showReason, setShowReason] = useState(false);
  const [busy, setBusy] = useState(false);

  const setF = (k, v) => setForm((cur) => ({ ...cur, [k]: v }));

  const openConfirm = () => {
    onError(null);
    if (!form.priceKey.trim()) { onError('priceKey is required.'); return; }
    if (!/^price_[A-Za-z0-9]+$/.test(form.stripePriceId.trim())) { onError('stripePriceId must match ^price_[A-Za-z0-9]+$.'); return; }
    const amount = parseInt(form.amountCents, 10);
    if (!Number.isInteger(amount) || amount < 0) { onError('amountCents must be a non-negative integer.'); return; }
    setShowReason(true);
  };

  const submit = async (changeReason) => {
    setBusy(true);
    try {
      const res = await api.adminPlans.updatePrice(planKey, {
        priceKey: form.priceKey.trim(),
        stripePriceId: form.stripePriceId.trim(),
        amountCents: parseInt(form.amountCents, 10),
        currency: form.currency.trim() || 'eur',
        billingInterval: form.billingInterval.trim() || 'monthly',
        isDefault: form.isDefault,
        changeReason,
      });
      setShowReason(false);
      setForm({ priceKey: '', stripePriceId: '', amountCents: '', currency: 'eur', billingInterval: 'monthly', isDefault: false });
      onSaved(res);
    } catch (e) {
      onError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={subCard}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Prices</div>

      {priceDriftWarning && <AmberNote>{priceDriftWarning}</AmberNote>}

      {prices.length === 0 ? (
        <p style={{ fontSize: 12.5, color: 'var(--text-muted, #9a9aa2)', margin: '0 0 10px' }}>No prices configured.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
          {prices.map((p) => (
            <div key={p.priceKey} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
              fontSize: 12.5, padding: '8px 10px', borderRadius: 8, background: 'var(--surface, #16161a)',
              border: '1px solid var(--border, #2a2a30)', opacity: p.isActive ? 1 : 0.5,
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontWeight: 600 }}>
                  {p.priceKey} {p.isDefault && <span style={{ color: 'var(--gold, #caa24a)' }}>· default</span>}
                  {!p.isActive && <span style={{ color: '#9a9aa2' }}> · inactive</span>}
                </span>
                <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace', color: 'var(--text-muted, #9a9aa2)' }}>{p.stripePriceId}</span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div>{fmtCents(p.amountCents, p.currency)}</div>
                <div style={{ color: 'var(--text-muted, #9a9aa2)' }}>{p.billingInterval}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <label style={label}>Price key</label>
          <input style={input} value={form.priceKey} disabled={disabled} onChange={(e) => setF('priceKey', e.target.value)} placeholder="monthly_default" />
        </div>
        <div>
          <label style={label}>Stripe price id</label>
          <input style={input} value={form.stripePriceId} disabled={disabled} onChange={(e) => setF('stripePriceId', e.target.value)} placeholder="price_..." />
        </div>
        <div>
          <label style={label}>Amount (cents)</label>
          <input type="number" style={input} value={form.amountCents} disabled={disabled} onChange={(e) => setF('amountCents', e.target.value)} placeholder="30000" />
        </div>
        <div>
          <label style={label}>Currency</label>
          <input style={input} value={form.currency} disabled={disabled} onChange={(e) => setF('currency', e.target.value)} placeholder="eur" />
        </div>
        <div>
          <label style={label}>Billing interval</label>
          <input style={input} value={form.billingInterval} disabled={disabled} onChange={(e) => setF('billingInterval', e.target.value)} placeholder="monthly" />
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={form.isDefault} disabled={disabled} onChange={(e) => setF('isDefault', e.target.checked)} />
            Set as default price
          </label>
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        <button type="button" style={btnGhost(disabled || busy)} disabled={disabled || busy} onClick={openConfirm}>
          Save price
        </button>
      </div>

      {showReason && (
        <ChangeReasonModal
          title={`Update price "${form.priceKey}" for ${planKey}`}
          confirmLabel="Save price"
          busy={busy}
          onCancel={() => setShowReason(false)}
          onConfirm={submit}
        />
      )}
    </div>
  );
}

// ─── history section ────────────────────────────────────────────────────────

function HistorySection({ planKey, history, disabled, onRolledBack, onError }) {
  const [rollbackTarget, setRollbackTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (changeReason) => {
    setBusy(true);
    try {
      const res = await api.adminPlans.rollback(planKey, { toVersion: rollbackTarget.version, changeReason });
      setRollbackTarget(null);
      onRolledBack(res);
    } catch (e) {
      onError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={subCard}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>History</div>
      {history.length === 0 ? (
        <p style={{ fontSize: 12.5, color: 'var(--text-muted, #9a9aa2)', margin: 0 }}>No recorded versions yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {history.map((h) => (
            <div key={h.id} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
              fontSize: 12.5, padding: '8px 10px', borderRadius: 8, background: 'var(--surface, #16161a)',
              border: '1px solid var(--border, #2a2a30)',
            }}>
              <div>
                <div><strong>v{h.version}</strong> · {h.changedBy || 'unknown'}</div>
                <div style={{ color: 'var(--text-muted, #9a9aa2)' }}>{fmtDate(h.createdAt)}</div>
                {h.changeReason && <div style={{ color: 'var(--text-muted, #9a9aa2)', marginTop: 2 }}>{h.changeReason}</div>}
              </div>
              <button type="button" style={btnDanger(disabled)} disabled={disabled} onClick={() => setRollbackTarget(h)}>
                Rollback
              </button>
            </div>
          ))}
        </div>
      )}

      {rollbackTarget && (
        <ChangeReasonModal
          title={`Roll back "${planKey}" to version ${rollbackTarget.version}?`}
          danger
          confirmLabel={`Roll back to v${rollbackTarget.version}`}
          busy={busy}
          onCancel={() => setRollbackTarget(null)}
          onConfirm={submit}
        />
      )}
    </div>
  );
}

// ─── plan card ───────────────────────────────────────────────────────────────

function PlanCard({ plan, disabledAll, onError, onPlanUpdated }) {
  const [limitsDraft, setLimitsDraft] = useState(() => ({ ...(plan.limits || {}) }));
  const [featuresDraft, setFeaturesDraft] = useState(() => ({ ...(plan.features || {}) }));
  const [displayName, setDisplayName] = useState(plan.displayName || '');
  const [description, setDescription] = useState(plan.description || '');
  const [priceDisplayCents, setPriceDisplayCents] = useState(plan.priceDisplayCents ?? '');
  const [priceDisplayWasCents, setPriceDisplayWasCents] = useState(plan.priceDisplayWasCents ?? '');
  const [promoBadgeText, setPromoBadgeText] = useState(plan.promoBadgeText || '');
  const [promoThenText, setPromoThenText] = useState(plan.promoThenText || '');
  const [showReason, setShowReason] = useState(false);
  const [busy, setBusy] = useState(false);
  const [diff, setDiff] = useState(null);
  const [conflict, setConflict] = useState(false);

  // Reset local drafts whenever the server-confirmed plan object changes
  // (e.g. after a save, rollback, or list reload) — no optimistic UI.
  useEffect(() => {
    setLimitsDraft({ ...(plan.limits || {}) });
    setFeaturesDraft({ ...(plan.features || {}) });
    setDisplayName(plan.displayName || '');
    setDescription(plan.description || '');
    setPriceDisplayCents(plan.priceDisplayCents ?? '');
    setPriceDisplayWasCents(plan.priceDisplayWasCents ?? '');
    setPromoBadgeText(plan.promoBadgeText || '');
    setPromoThenText(plan.promoThenText || '');
    setConflict(false);
  }, [plan]);

  const disabled = disabledAll || !plan.editable;

  const submit = async (changeReason) => {
    setBusy(true);
    onError(null);
    try {
      const res = await api.adminPlans.update(plan.planKey, {
        limits: limitsDraft,
        features: featuresDraft,
        displayName: displayName.trim() || undefined,
        description: description.trim() === '' ? null : description,
        priceDisplayCents: priceDisplayCents === '' ? null : parseInt(priceDisplayCents, 10),
        priceDisplayWasCents: priceDisplayWasCents === '' ? null : parseInt(priceDisplayWasCents, 10),
        promoBadgeText: promoBadgeText.trim() === '' ? null : promoBadgeText,
        promoThenText: promoThenText.trim() === '' ? null : promoThenText,
        changeReason,
        expectedVersion: plan.version,
      });
      setShowReason(false);
      setDiff(res.applied || null);
      onPlanUpdated(plan.planKey, res);
    } catch (e) {
      if (e instanceof DathentAPIError && e.status === 409) {
        setConflict(true);
        setShowReason(false);
      } else {
        onError(errMsg(e));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: 18 }}>{plan.displayName}</h2>
            <span style={{ fontSize: 11, fontFamily: 'ui-monospace, SFMono-Regular, monospace', color: 'var(--text-muted, #9a9aa2)' }}>{plan.planKey}</span>
            <span style={{
              fontSize: 11, padding: '2px 8px', borderRadius: 999, border: '1px solid var(--border, #2a2a30)',
              color: 'var(--text-muted, #9a9aa2)',
            }}>v{plan.version}</span>
            <span style={{
              fontSize: 11, padding: '2px 8px', borderRadius: 999,
              background: plan.isActive ? '#30a46c22' : '#e0685f22',
              color: plan.isActive ? '#7ee2a8' : '#e8a59f',
            }}>{plan.isActive ? 'active' : 'inactive'}</span>
            {!plan.editable && (
              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, color: 'var(--text-muted, #9a9aa2)', border: '1px solid var(--border, #2a2a30)' }}>
                non-editable
              </span>
            )}
          </div>
        </div>
      </div>

      {conflict && (
        <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 10, fontSize: 13, background: '#caa24a18', border: '1px solid #caa24a55', color: '#e7c878', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <span>Plan changed by someone else — reload to see the latest version before saving.</span>
          <button type="button" style={btn(false)} onClick={() => window.location.reload()}>Reload</button>
        </div>
      )}

      {!plan.editable ? (
        <p style={{ marginTop: 12, fontSize: 12.5, color: 'var(--text-muted, #9a9aa2)' }}>
          This plan is structurally non-editable and has no billing_plans row to configure.
        </p>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 4 }}>
            <div>
              <label style={label}>Display name</label>
              <input style={input} value={displayName} disabled={disabled} onChange={(e) => setDisplayName(e.target.value)} />
            </div>
            <div>
              <label style={label}>Description</label>
              <input style={input} value={description} disabled={disabled} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div>
              <label style={label}>Display price (cents)</label>
              <input type="number" style={input} value={priceDisplayCents} disabled={disabled} onChange={(e) => setPriceDisplayCents(e.target.value)} placeholder="e.g. 30000" />
            </div>
            <div>
              <label style={label}>Display "was" price (cents)</label>
              <input type="number" style={input} value={priceDisplayWasCents} disabled={disabled} onChange={(e) => setPriceDisplayWasCents(e.target.value)} placeholder="optional" />
            </div>
            <div>
              <label style={label}>Promo badge text</label>
              <input style={input} value={promoBadgeText} disabled={disabled} onChange={(e) => setPromoBadgeText(e.target.value)} placeholder="optional" />
            </div>
            <div>
              <label style={label}>Promo "then" text</label>
              <input style={input} value={promoThenText} disabled={disabled} onChange={(e) => setPromoThenText(e.target.value)} placeholder="optional" />
            </div>
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted, #9a9aa2)', marginTop: 6 }}>
            {plan.priceDisplayCurrency || 'eur'} · {plan.priceDisplayUnit || 'monthly'}
          </div>

          <LimitsTable limits={plan.limits || {}} draft={limitsDraft} setDraft={setLimitsDraft} disabled={disabled} />
          <FeaturesToggles draft={featuresDraft} setDraft={setFeaturesDraft} disabled={disabled} />

          <div style={{ marginTop: 14 }}>
            <button type="button" style={btn(disabled || busy)} disabled={disabled || busy} onClick={() => setShowReason(true)}>
              {busy ? 'Saving…' : 'Save changes'}
            </button>
          </div>

          <DiffSummary applied={diff} />

          {showReason && (
            <ChangeReasonModal
              title={`Save changes to "${plan.displayName}"`}
              confirmLabel="Save changes"
              busy={busy}
              onCancel={() => setShowReason(false)}
              onConfirm={submit}
            />
          )}

          <PricesSection
            planKey={plan.planKey}
            prices={plan.prices || []}
            priceDriftWarning={plan.priceDriftWarning}
            disabled={disabled}
            onSaved={(res) => onPlanUpdated(plan.planKey, res)}
            onError={onError}
          />

          <HistorySection
            planKey={plan.planKey}
            history={plan.history || []}
            disabled={disabled}
            onRolledBack={(res) => onPlanUpdated(plan.planKey, res)}
            onError={onError}
          />
        </>
      )}
    </div>
  );
}

// ─── screen shell ───────────────────────────────────────────────────────────

export function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.adminPlans.list();
      setPlans(res.plans || []);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Any successful mutation (save/price/rollback) returns { planKey,
  // newVersion, ... } but never the full updated plan row — re-fetch the
  // whole list so every card (limits/features/prices/history) reflects the
  // authoritative server state. No optimistic patching.
  const onPlanUpdated = useCallback(() => { load(); }, [load]);

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '8px 4px 64px' }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--gold, #caa24a)' }}>Admin · internal</div>
        <h1 style={{ margin: '4px 0 2px', fontSize: 26 }}>Plans &amp; limits</h1>
        <p style={{ margin: 0, color: 'var(--text-muted, #9a9aa2)', fontSize: 14 }}>
          Edit plan display pricing, usage limits and feature flags. Every save requires a change reason and is
          versioned; roll back to an earlier version from the history section below each plan.
        </p>
      </div>

      {loading ? (
        <div style={card}>Loading…</div>
      ) : error ? (
        <div style={card}><ErrorNote error={error} /></div>
      ) : plans.length === 0 ? (
        <div style={{ ...card, color: 'var(--text-muted, #9a9aa2)' }}>No plans found.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {plans.map((plan) => (
            <PlanCard key={plan.planKey} plan={plan} disabledAll={false} onError={setError} onPlanUpdated={onPlanUpdated} />
          ))}
        </div>
      )}

      <ErrorNote error={loading ? null : null} />
    </div>
  );
}

export default AdminPlans;
